import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { MenuModule } from 'primeng/menu';
import { MessageService, MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProjectFormService } from '../../services/projectform.service';
import { ProjectFormFolderModel } from '../../models/projectform.model';
import { ImportFormModalComponent } from './import-form-modal/import-form-modal';
import { CreateFormModalComponent } from './form-detail/create-form-modal/create-form-modal';
import { ProjectStateService } from '../../services/project-state.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';

@Component({
  selector: 'app-forms',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    MenuModule
  ],
  providers: [MessageService, DialogService],
  templateUrl: './forms.html',
  styleUrl: './forms.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  projectId!: number;
  formFolders: ProjectFormFolderModel[] = [];
  filteredFolders: ProjectFormFolderModel[] = [];
  newFormMenuItems: MenuItem[] = [];
  actionMenuItems: MenuItem[] = [];
  importDialogRef?: DynamicDialogRef;
  loading: boolean = false;
  searchValue: string = '';
  isReadOnly: boolean = false;
  canView: boolean = false;
  canCreate: boolean = false;
  canImport: boolean = false;
  canPublish: boolean = false;
  isProjectInactive: boolean = false;
  private dataLoaded = false;

  // Sort state
  sortField: string = '';
  sortOrder: 'asc' | 'desc' | '' = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectFormService: ProjectFormService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService,
    private permissionHelper: PermissionHelperService
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.projectId = +params['projectId'];
      if (this.projectId) {
        this.tryLoadFormFolders();
      }
    });

    this.projectStateService.currentProjectUserActive$.subscribe(isActive => {
      this.isProjectInactive = isActive === false;
      this.updateReadOnlyState();
    });

    this.projectStateService.pageSearch$.pipe(takeUntil(this.destroy$)).subscribe(search => {
      this.searchValue = search;
      this.applyFilterAndSort();
      this.cdr.markForCheck();
    });

    this.projectStateService.currentProjectPermissions$.subscribe(() => {
      this.canView = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.VIEW);
      this.canCreate = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.CREATE);
      this.canImport = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.IMPORT);
      this.canPublish = this.permissionHelper.has(PAGE_KEYS.FORMS, PERMISSION_KEYS.PUBLISH);
      this.updateReadOnlyState();
      this.tryLoadFormFolders();
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateReadOnlyState(): void {
    this.isReadOnly = this.isProjectInactive || (!this.canCreate && !this.canImport && !this.canPublish);
    this.updateNewFormMenu();
    this.cdr.markForCheck();
  }

  private tryLoadFormFolders(): void {
    if (this.dataLoaded || !this.projectId) return;
    if (!this.canView) return;
    this.loadFormFolders();
    this.dataLoaded = true;
  }

  loadFormFolders(): void {
    this.loading = true;
    this.projectFormService.getProjectFormFolders(this.projectId).subscribe({
      next: (folders) => {
        this.formFolders = folders;
        this.applyFilterAndSort();
        this.updateNewFormMenu();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading form folders:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load form folders'
        });
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  // ===== Search =====
  onSearch(): void {
    this.applyFilterAndSort();
    this.cdr.markForCheck();
  }

  clearSearch(): void {
    this.searchValue = '';
    this.applyFilterAndSort();
    this.cdr.markForCheck();
  }

  // ===== Sort =====
  onSort(field: string): void {
    if (this.sortField === field) {
      // Cycle: asc → desc → none
      if (this.sortOrder === 'asc') {
        this.sortOrder = 'desc';
      } else if (this.sortOrder === 'desc') {
        this.sortField = '';
        this.sortOrder = '';
      }
    } else {
      this.sortField = field;
      this.sortOrder = 'asc';
    }
    this.applyFilterAndSort();
    this.cdr.markForCheck();
  }

  getSortIcon(field: string): string {
    if (this.sortField !== field) {
      return 'ri-arrow-up-down-line';
    }
    return this.sortOrder === 'asc' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line';
  }

  // ===== Filter + Sort combined =====
  private applyFilterAndSort(): void {
    let result = [...this.formFolders];

    // Filter
    if (this.searchValue) {
      const query = this.searchValue.toLowerCase();
      result = result.filter(f =>
        f.templateName?.toLowerCase().includes(query) ||
        f.status?.toLowerCase().includes(query)
      );
    }

    // Sort
    if (this.sortField && this.sortOrder) {
      const field = this.sortField as keyof ProjectFormFolderModel;
      const dir = this.sortOrder === 'asc' ? 1 : -1;
      result.sort((a, b) => {
        const aVal = a[field] ?? '';
        const bVal = b[field] ?? '';
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return (aVal - bVal) * dir;
        }
        return String(aVal).localeCompare(String(bVal)) * dir;
      });
    }

    this.filteredFolders = result;
  }

  updateNewFormMenu(): void {
    const publishedFolders = this.formFolders.filter(f => f.status === 'Published');
    const unpublishedFolders = this.formFolders.filter(f => f.status === 'Unpublished');

    this.newFormMenuItems = [];

    if (!this.canCreate) {
      return;
    }

    if (this.canCreate && !this.isProjectInactive) {
      // Add published folders first (enabled, clickable)
      publishedFolders.forEach(folder => {
        this.newFormMenuItems.push({
          label: folder.templateName,
          command: () => this.createNewForm(folder)
        });
      });

      // Add separator if both published and unpublished exist
      if (publishedFolders.length > 0 && unpublishedFolders.length > 0) {
        this.newFormMenuItems.push({ separator: true });
      }

      // Add unpublished folders (disabled, greyed out)
      unpublishedFolders.forEach(folder => {
        this.newFormMenuItems.push({
          label: folder.templateName,
          disabled: true,
          style: { 'color': '#94a3b8' }
        });
      });
    }
  }

  createNewForm(folder: ProjectFormFolderModel): void {
    if (!this.canCreate || this.isProjectInactive) {
      return;
    }
    const dialogRef = this.dialogService.open(CreateFormModalComponent, {
      header: `New ${folder.templateName}`,
      width: '30vw',
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'auto',
        padding: '0'
      },
      data: {
        folderId: folder.id
      }
    });

    if (dialogRef) {
      dialogRef.onClose.subscribe((result) => {
        if (result) {
          this.router.navigate(['/form-detail', this.projectId, folder.id]);
        }
      });
    }
  }

  openImportModal(): void {
    if (!this.canImport || this.isProjectInactive) {
      return;
    }
    const dialogRef = this.dialogService.open(ImportFormModalComponent, {
      header: 'Import Forms from Account',
      width: '45vw',
      modal: true,
      closable: true,
      styleClass: 'kansei-dialog',
      contentStyle: {
        overflow: 'auto',
        padding: '0'
      },
      data: {
        projectId: this.projectId
      }
    });

    if (dialogRef) {
      this.importDialogRef = dialogRef;
      dialogRef.onClose.subscribe((result) => {
        if (result) {
          this.loadFormFolders();
        }
      });
    }
  }

  getStatusClass(status?: string): string {
    return status === 'Published' ? 'status-badge--success' : 'status-badge--secondary';
  }

  navigateToFormDetail(folderId: number): void {
    this.router.navigate(['/form-detail', this.projectId, folderId]);
  }

  showActionMenu(event: Event, menu: any, folder: ProjectFormFolderModel): void {
    if (!this.canPublish || this.isProjectInactive) {
      return;
    }
    const isPublished = folder.status === 'Published';
    this.actionMenuItems = [
      {
        label: isPublished ? 'Unpublish' : 'Publish',
        icon: isPublished ? 'pi pi-eye-slash' : 'pi pi-eye',
        command: () => this.toggleStatus(folder)
      }
    ];
    menu.toggle(event);
  }

  toggleStatus(folder: ProjectFormFolderModel): void {
    if (!this.canPublish || this.isProjectInactive) {
      return;
    }
    const newStatus = folder.status === 'Published' ? 'Unpublished' : 'Published';
    const request = {
      status: newStatus,
      updatedBy: 1 // TODO: Get actual user ID
    };

    this.projectFormService.updateFormFolderStatus(folder.id, request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Template ${newStatus.toLowerCase()} successfully`
        });
        this.loadFormFolders();
      },
      error: (error) => {
        console.error('Error updating form folder status:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update status'
        });
      }
    });
  }
}
